import {useCallback, useContext, useEffect, useState} from "react";
import {
  Badge,
  Box,
  Flex,
  Grid,
  HStack,
  IconButton,
  Text,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import {FiBattery, FiClock, FiCpu, FiRefreshCw, FiThermometer} from "react-icons/fi";
import {AdapterContext, ConnectionContext} from "../Contexts";

const emptyStatus = {
  battery: null,
  temperature: null,
  temperatureMode: 0,
  firmwareVersion: null,
};

const emptyClock = {
  syncedAt: null,
  error: null,
  timezone: "Local time",
};

function formatTime(date, includeSeconds = false) {
  return new Intl.DateTimeFormat([], {
    hour: "2-digit",
    minute: "2-digit",
    second: includeSeconds ? "2-digit" : undefined,
  }).format(date);
}

function Metric({icon: Icon, label, value, meta, accent = "#9aa0a8", tooltip}) {
  const content = (
    <Box
      position="relative"
      minW={0}
      minH="112px"
      p={{base: 3, md: 4}}
      pt={{base: 4, md: 5}}
      bg="#111315"
      border="1px solid"
      borderColor="#292d31"
      borderRadius="5px"
      overflow="hidden"
    >
      <Box position="absolute" top={0} left={0} width="42px" height="2px" bg={accent} />
      <HStack spacing={2} color="#717780" mb={3}>
        <Icon size="15px" />
        <Text fontSize="10px" fontWeight="bold" textTransform="uppercase" noOfLines={1}>{label}</Text>
      </HStack>
      <Text fontSize={{base: "xl", md: "2xl"}} lineHeight="1" fontWeight="800" color="#f0f1f2" noOfLines={1}>
        {value}
      </Text>
      <Text mt={2} minH="27px" fontSize="10px" lineHeight="1.35" color="#666c74" noOfLines={2}>{meta}</Text>
    </Box>
  );

  return tooltip ? <Tooltip label={tooltip} hasArrow>{content}</Tooltip> : content;
}

export function DeviceTelemetry() {
  const adapter = useContext(AdapterContext);
  const connected = useContext(ConnectionContext);
  const toast = useToast();
  const [status, setStatus] = useState(emptyStatus);
  const [clock, setClock] = useState(emptyClock);
  const [now, setNow] = useState(new Date());
  const [refreshing, setRefreshing] = useState(false);

  const readState = useCallback(() => {
    if (!connected) {
      setStatus(emptyStatus);
      setClock(emptyClock);
      return;
    }
    setStatus(adapter.getStatus());
    setClock(adapter.getClockStatus?.() ?? emptyClock);
  }, [adapter, connected]);

  useEffect(() => {
    readState();
    return adapter.onCommand(readState);
  }, [adapter, readState]);

  useEffect(() => {
    if (!connected) return undefined;
    const updateClock = () => {
      setNow(new Date());
      setClock(adapter.getClockStatus?.() ?? emptyClock);
    };
    updateClock();
    const interval = setInterval(updateClock, 1000);
    return () => clearInterval(interval);
  }, [adapter, connected]);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await adapter.refreshStatus();
      readState();
    } catch (error) {
      toast({title: "Refresh failed", description: error.message, status: "error", duration: 4000});
    } finally {
      setRefreshing(false);
    }
  };

  const clockMeta = !connected
    ? "Connect to synchronize"
    : clock.error
      ? "Sync unavailable"
      : clock.syncedAt
        ? `Synced ${formatTime(new Date(clock.syncedAt))}`
        : "Sync pending";

  return (
    <Box>
      <Flex align="center" justify="space-between" gap={3} mb={3}>
        <HStack spacing={2}>
          <Text fontSize="10px" color="#6e747c" textTransform="uppercase" fontWeight="800">Device status</Text>
          <Badge colorScheme={connected ? "green" : "gray"} variant="subtle">
            {connected ? "Live" : "Offline"}
          </Badge>
        </HStack>
        <Tooltip label="Refresh device status" hasArrow>
          <IconButton
            aria-label="Refresh device status"
            icon={<FiRefreshCw />}
            size="sm"
            variant="ghost"
            borderRadius="5px"
            isDisabled={!connected}
            isLoading={refreshing}
            onClick={refresh}
          />
        </Tooltip>
      </Flex>

      <Grid templateColumns={{base: "repeat(2, minmax(0, 1fr))", md: "repeat(4, minmax(0, 1fr))"}} gap={3}>
        <Metric
          icon={FiBattery}
          label="Battery"
          value={connected && status.battery != null ? `${status.battery}%` : "--"}
          meta={connected ? "Remaining charge" : "No device data"}
          accent={status.battery != null && status.battery <= 20 ? "#ff9d5c" : "#5ed5a8"}
        />
        <Metric
          icon={FiThermometer}
          label="Temperature"
          value={connected && status.temperature != null ? `${status.temperature} °${status.temperatureMode ? "F" : "C"}` : "--"}
          meta={connected ? "Current reading" : "No device data"}
          accent="#ffd84d"
        />
        <Metric
          icon={FiCpu}
          label="Firmware"
          value={connected ? status.firmwareVersion ?? "--" : "--"}
          meta={connected ? "Device version" : "No device data"}
          accent="#5ed5c8"
        />
        <Metric
          icon={FiClock}
          label="Device clock"
          value={connected ? formatTime(now, true) : "--"}
          meta={clockMeta}
          accent={clock.error ? "#ff9d5c" : "#8bb7ff"}
          tooltip={clock.error ?? clock.timezone}
        />
      </Grid>
    </Box>
  );
}
